import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Facebook, Instagram, Twitter, Linkedin, Youtube, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Eyebrow, FadeUp, MaskedLines } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, x: Twitter, linkedin: Linkedin, youtube: Youtube };

export default function Contact() {
  const { settings } = useSettings();
  const { contact, social } = settings || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const liveSocials = (social || []).filter((s) => s.url);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    }
    setSending(false);
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6 lg:px-12 lg:pb-28 lg:pt-20" data-testid="contact-main">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="contact-title">
              <MaskedLines baseDelay={0.2} lines={[<>We would love</>, <span className="bg-gradient-to-r from-cyan-300 to-indigo-400 bg-clip-text text-transparent">to hear from you</span>]} />
            </h1>
            <p className="mt-6 max-w-md text-base text-slate-400" data-testid="contact-subline">
              We will be pleased to answer all your questions and assist with your project.
            </p>

            <div className="mt-10 space-y-5">
              {contact?.phone && (
                <a href={`tel:${contact.phone.replace(/\s+/g, "")}`} className="group flex items-center gap-4" data-testid="contact-phone-link">
                  <span className="rounded-xl border border-white/10 bg-ink2 p-3 text-cyan-400 transition group-hover:border-cyan-400/40"><Phone className="h-5 w-5" /></span>
                  <span className="text-sm text-slate-300 group-hover:text-white">{contact.phone}</span>
                </a>
              )}
              {contact?.email && (
                <a href={`mailto:${contact.email}`} className="group flex items-center gap-4" data-testid="contact-email-link">
                  <span className="rounded-xl border border-white/10 bg-ink2 p-3 text-cyan-400 transition group-hover:border-cyan-400/40"><Mail className="h-5 w-5" /></span>
                  <span className="text-sm text-slate-300 group-hover:text-white">{contact.email}</span>
                </a>
              )}
              {contact?.address && (
                <div className="flex items-center gap-4" data-testid="contact-address-row">
                  <span className="rounded-xl border border-white/10 bg-ink2 p-3 text-cyan-400"><MapPin className="h-5 w-5" /></span>
                  <span className="text-sm text-slate-300">{contact.address}</span>
                </div>
              )}
              <div className="flex items-center gap-4" data-testid="contact-hours-row">
                <span className="rounded-xl border border-white/10 bg-ink2 p-3 text-cyan-400"><Clock className="h-5 w-5" /></span>
                <span className="text-sm text-slate-300">Our team is ready to help 24/7</span>
              </div>
            </div>

            {liveSocials.length > 0 && (
              <div className="mt-10 flex gap-3">
                {liveSocials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.platform?.toLowerCase()] || Globe;
                  return (
                    <a key={s.platform + s.url} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform} data-testid={`contact-social-${s.platform?.toLowerCase()}-link`} className="rounded-full border border-white/10 p-3 text-slate-300 transition hover:border-cyan-400/50 hover:text-cyan-300">
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            <FadeUp className="rounded-3xl border border-white/10 bg-ink2/70 p-6 sm:p-10" data-testid="contact-form-card">
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="contact-name">Name *</label>
                    <Input id="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className="border-white/10 bg-ink/60" data-testid="contact-name-input" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="contact-email">Email *</label>
                    <Input id="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="border-white/10 bg-ink/60" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="contact-phone">Phone</label>
                  <Input id="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+1 (___) ___-____" className="border-white/10 bg-ink/60" data-testid="contact-phone-input" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="contact-message">Project *</label>
                  <Textarea id="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your dream — brand, software, campaign, event…" rows={6} className="resize-none border-white/10 bg-ink/60" data-testid="contact-message-textarea" />
                </div>
                <Button type="submit" disabled={sending} data-testid="contact-submit-button" className="w-full rounded-full bg-cyan-400 py-6 text-sm font-semibold text-ink hover:bg-cyan-300 sm:w-auto sm:px-10">
                  {sending ? "Sending…" : <>Send message <Send className="ml-2 h-4 w-4" /></>}
                </Button>
              </form>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
